import { Flex, font, themedColor } from '@lemonade-hq/cdk';
import { I18nContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import Image from 'next/image';
import { rgba } from 'polished';
import type React from 'react';
import { useContext } from 'react';
import styled, { css } from 'styled-components';
import type { CustomersReviewsVariant } from '..';

const SHADOW_OPACITY_1 = 0.06;
const SHADOW_OPACITY_2 = 0.16;

const latoItalic = css`
  /* latin-ext */
  @font-face {
    font-family: 'Lato';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/lato/v25/S6u8w4BMUTPHjxsAUi-qNiXg7eU0.woff2) format('woff2');
    unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF,
      U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Lato';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/lato/v25/S6u8w4BMUTPHjxsAXC-qNiXg7Q.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329,
      U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
`;

const defaultCardStyles = css`
  height: ${({ shortCard }: { readonly shortCard: boolean }) => (shortCard ? '200px' : '255px')};
  box-shadow:
    0 1px 8px 0 ${({ theme }) => rgba(theme.colors.black, SHADOW_OPACITY_1)},
    0 31px 30px -18px ${({ theme }) => rgba(theme.colors.black, SHADOW_OPACITY_2)};
  border-radius: 10px;
  padding: 20px;
`;

const modernCardStyles = css`
  width: 231px;
  height: 139px;
  border-radius: 12px;
  padding: 30px;
  justify-content: space-between;
`;

const TweetCardCmp = styled(Flex)<{ readonly shortCard: boolean; readonly $variant: CustomersReviewsVariant }>`
  background: ${themedColor('white')};
  width: 338px;
  box-sizing: content-box;

  &.tweet-card {
    min-height: 0;
  }

  ${({ $variant }) => ($variant === 'modern' ? modernCardStyles : defaultCardStyles)}
`;

const TweetImage = styled(Image)`
  width: 5.4rem;
  height: 5.4rem;
  padding: 2px;
  position: relative;
  top: -5px;
  border-radius: 50%;
`;

const Name = styled.p`
  text-align: left;
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.2rem', fontWeight: 700 })};
`;

const Handle = styled.p`
  text-align: left;
  ${font('main', { fontSize: '1.4rem', lineHeight: '2.2rem', fontWeight: 700 })};
  color: ${themedColor('metal')};
`;

const ModernHandle = styled.p`
  text-align: left;
  ${font('main', { fontSize: '1.6rem' })};
  color: #b7b7b7;
`;

const TweetText = styled.p`
  text-align: left;
  ${font('main', { fontSize: '1.6rem', lineHeight: '2.4rem' })}

  .primary {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModernTweetText = styled.p`
  ${latoItalic}
  text-align: left;
  ${font('main', { fontSize: '2.4rem', lineHeight: '1.3' })}
  font-style: italic;
  letter-spacing: -0.24px;
  color: #4a4a4a;
`;

export interface Tweet {
  readonly img: StaticImageData | string;
  readonly handle: string;
  readonly name: string;
  readonly text?: string;
}

interface TweetCardProps {
  readonly tweet: Tweet;
  readonly className?: string;
  readonly shortCard?: boolean;
  readonly variant?: CustomersReviewsVariant;
}

export const TweetCard: React.FC<TweetCardProps> = ({
  tweet,
  className = '',
  shortCard = true,
  variant = 'default',
}) => {
  const { img, handle, name, text } = tweet;
  const { s } = useContext(I18nContext);

  if (variant === 'modern') {
    return (
      <TweetCardCmp
        $variant={variant}
        className={`tweet-card ${className}`}
        flexDirection="column"
        m="8px"
        shortCard={shortCard}
      >
        <ModernTweetText>{parse(text ?? s(`hp.customer_reviews.tweets.${handle}`))}</ModernTweetText>
        <ModernHandle>{handle}</ModernHandle>
      </TweetCardCmp>
    );
  }

  return (
    <TweetCardCmp
      $variant={variant}
      className={`tweet-card ${className}`}
      flexDirection="column"
      m="15px"
      shortCard={shortCard}
    >
      <Flex flexDirection="row">
        <TweetImage alt={handle} src={img} />
        <Flex flex="1" flexDirection="column" pl="10px">
          <Name>{name}</Name>
          <Handle>{handle}</Handle>
        </Flex>
      </Flex>
      <TweetText>{parse(text ?? s(`hp.customer_reviews.tweets.${handle}`))}</TweetText>
    </TweetCardCmp>
  );
};
